// src/components/common/Header.tsx

import { useContext, useMemo, useState, type MouseEvent } from 'react';

import { useNavigate } from 'react-router-dom';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';

import { useRecoilValue } from 'recoil';
import { userState } from '@recoils/atoms';
import { UserGroup } from '@models/user';

import useAuth from '@hooks/useAuth';
import { usePartnerDetail } from '@queries/partners/usePartnerDetail';
import { toUsdDisplay } from '@utils/money';
import { ColorModeContext } from '../../theme/ColorModeContext';

type HeaderProps = {
  /** Callback to toggle the left sidebar drawer (used on mobile) */
  onToggleDrawer: () => void;
};

/**
 * Global application header.
 * - Brand + mobile drawer toggle
 * - Partner balance badge (partner users only)
 * - User menu (change password / logout)
 * - Light/Dark theme toggle
 */
function Header({ onToggleDrawer }: HeaderProps) {
  const { logOut } = useAuth();
  const user = useRecoilValue(userState);

  const navigate = useNavigate();

  // Responsive breakpoints
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  // Color mode (light/dark)
  const { mode, toggleColorMode } = useContext(ColorModeContext);

  // Account menu anchor
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // Logout dialog state (✅ Recommended: avoid window.confirm)
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  /**
   * Guard partner-only derived values.
   * Avoid running partner queries with invalid IDs (e.g., 0 / NaN).
   */
  const isPartner = user?.group === UserGroup.PARTNER;
  const partnerId = isPartner && user?.partnerId ? Number(user.partnerId) : 0;

  /**
   * Partner details (name/balance)
   * - Hook is already `enabled: !!partnerId` (in your implementation)
   */
  const { data: partnerDetailForm } = usePartnerDetail(
    partnerId,
    // Poll every 30 seconds (keeps balance fresh)
    30 * 1000
  );

  const partnerInfo = useMemo(() => {
    if (!isPartner) return null;
    return partnerDetailForm?.data?.[0] ?? null;
  }, [isPartner, partnerDetailForm]);

  // Role label mapping (human-friendly)
  const groupLabel = useMemo(() => {
    if (!user) return undefined;

    const labels: Record<UserGroup, string> = {
      [UserGroup.ADMINISTRATOR]: 'Admin',
      [UserGroup.MANAGER]: 'Manager',
      [UserGroup.PRODUCT_MANAGER]: 'Product Manager',
      [UserGroup.PARTNER]: 'Partner',
    };

    return labels[user.group];
  }, [user]);

  /** Open user menu (avatar dropdown) */
  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  /** Close user menu */
  const handleCloseUserMenu = () => {
    setUserMenuAnchor(null);
  };

  /** From user menu: open change password dialog */
  const handleClickChangePassword = () => {
    handleCloseUserMenu();
    navigate('/account/change-password');
  };

  /** From user menu: open logout confirm dialog */
  const handleClickLogout = () => {
    handleCloseUserMenu();
    setLogoutOpen(true);
  };

  /** Close logout dialog (✅ Recommended: disable close while processing) */
  const handleCloseLogout = () => {
    if (logoutLoading) return;
    setLogoutOpen(false);
  };

  /** Confirm logout */
  const handleConfirmLogout = async () => {
    if (logoutLoading) return;

    try {
      setLogoutLoading(true);

      // `logOut()` currently navigates immediately (sync),
      // but we keep loading state to prevent double-clicks.
      logOut();
    } finally {
      // In most cases, navigation happens right away.
      setLogoutLoading(false);
      setLogoutOpen(false);
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            px: 2,
          }}
        >
          {/* Left: mobile drawer toggle + brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {!isSmUp && (
              <IconButton color="inherit" edge="start" onClick={onToggleDrawer} sx={{ mr: 0.5 }}>
                <MenuIcon />
              </IconButton>
            )}

            <Typography variant="h6" noWrap sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
              eSIM Station
            </Typography>
          </Box>

          {/* Right: partner badge + user info + theme toggle + avatar menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Partner badge (partner users only, >= sm screens) */}
            {partnerInfo && (
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  px: 1.25,
                  py: 0.75,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 999,
                  bgcolor: 'action.hover',
                  maxWidth: 320,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.1 }}
                  >
                    Partner
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.2,
                    }}
                  >
                    {partnerInfo.name}
                  </Typography>
                </Box>

                <Divider orientation="vertical" flexItem sx={{ mx: 1.25, borderColor: 'divider' }} />

                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.1 }}
                  >
                    Balance
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {toUsdDisplay(partnerInfo.balance || 0)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* User display (>= md screens) */}
            {user && (
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  maxWidth: 260,
                }}
              >
                <Link
                  component="button"
                  variant="body2"
                  color="inherit"
                  sx={{
                    cursor: 'default',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: 13,
                  }}
                >
                  {user.name} ({user.username})
                </Link>

                {groupLabel && (
                  <Typography variant="caption" sx={{ mt: 0.25, color: 'text.secondary' }}>
                    {groupLabel}
                  </Typography>
                )}
              </Box>
            )}

            {user && isMdUp && (
              <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'divider' }} />
            )}

            {/* Theme toggle */}
            <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
              <IconButton
                onClick={toggleColorMode}
                size="small"
                sx={{
                  ml: 0.5,
                  transition: 'transform 0.15s ease',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
              >
                {mode === 'light' ? (
                  <Brightness4Icon fontSize="small" />
                ) : (
                  <Brightness7Icon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            {/* Avatar menu */}
            {user && (
              <>
                <Tooltip title="Account">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0.5 }} size="large">
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'primary.main',
                        fontSize: 16,
                      }}
                    >
                      {user.name?.[0]?.toUpperCase() || <AccountCircleIcon fontSize="small" />}
                    </Avatar>
                  </IconButton>
                </Tooltip>

                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleCloseUserMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  keepMounted
                >
                  {/* Partner info (visible on all breakpoints via menu) */}
                  {partnerInfo && (
                    <MenuItem disabled sx={{ display: 'block', py: 1, maxWidth: 260 }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {partnerInfo.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, mt: 0.25, color: 'text.secondary' }}>
                        {toUsdDisplay(partnerInfo.balance || 0)}
                      </Typography>
                    </MenuItem>
                  )}

                  {/* Logged-in user display */}
                  <MenuItem
                    disabled
                    sx={{
                      fontSize: 13,
                      maxWidth: 260,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {user.name} ({user.username})
                  </MenuItem>

                  <Divider sx={{ my: 0.5 }} />

                  <MenuItem onClick={handleClickChangePassword}>Change password</MenuItem>
                  <MenuItem onClick={handleClickLogout}>Logout</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* ✅ Logout confirmation (MUI) */}
      <Dialog
        open={logoutOpen}
        onClose={handleCloseLogout}
        maxWidth="xs"
        fullWidth
        // ✅ Recommended: prevent accidental dismiss while confirming
        disableEscapeKeyDown={logoutLoading}
      >
        <DialogTitle>Logout</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to log out?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogout} variant="outlined" disabled={logoutLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            color="error"
            startIcon={<LogoutIcon />}
            disabled={logoutLoading}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Header;
